import React from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, change } from 'redux-form'
import { bindActionCreators } from 'redux'
import { SubmissionError } from 'redux-form'
import LoadingProgress from '../components/LoadingProgress'

import { fetchRemoteActions, addRemoteAction } from '../actions/remoteactions'
import { renderInputField, renderSelectField } from '../utils/forms'

const ACTION_META = {
  SINGLE_DOOR_OPEN: {
    label: 'Abrir locker',
    iconClass: 'fas fa-door-open',
    description: 'Se abrirá remotamente la puerta del locker.',
    confirmText: 'Confirmar apertura',
    accentColor: '#10b981',
    bgGradient: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
    warning: null,
  },
  SINGLE_UNRENT: {
    label: 'Desalquilar',
    iconClass: 'fas fa-sign-out-alt',
    description: 'El locker quedará libre y disponible para nuevos alquileres.',
    confirmText: 'Confirmar desalquiler',
    accentColor: '#ef4444',
    bgGradient: 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
    warning: 'Esta acción liberará el locker. El cliente perderá el acceso inmediatamente.',
  },
  SINGLE_BLOCKLATER: {
    label: 'Marcar QR / PDA',
    iconClass: 'fas fa-qrcode',
    description: 'El locker quedará reservado para acceso mediante código QR o terminal PDA.',
    confirmText: 'Confirmar reserva',
    accentColor: '#8b5cf6',
    bgGradient: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)',
    warning: null,
  },
  SINGLE_RESET_PIN: {
    label: 'Cambiar PIN',
    iconClass: 'fas fa-lock',
    description: 'Se restablecerá el PIN del locker.',
    confirmText: 'Confirmar cambio de PIN',
    accentColor: '#f59e0b',
    bgGradient: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
    warning: null,
  },
  SINGLE_NIGHTSCAN: {
    label: 'Vincular',
    iconClass: 'fas fa-globe',
    description: 'Se realizará un nightscan individual para vincular el locker.',
    confirmText: 'Confirmar vinculación',
    accentColor: '#dc2626',
    bgGradient: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
    warning: 'Acción crítica. El locker realizará un ciclo de vinculación completo.',
  },
  SINGLE_KERONG_PROGRAM_OTA: {
    label: 'Program OTA',
    iconClass: 'fas fa-microchip',
    description: 'Actualización del firmware de programa del locker vía OTA.',
    confirmText: 'Confirmar OTA Programa',
    accentColor: '#dc2626',
    bgGradient: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
    warning: 'Acción crítica. El locker quedará fuera de servicio durante la actualización.',
  },
  SINGLE_KERONG_BOOTLOADER_OTA: {
    label: 'Bootloader OTA',
    iconClass: 'fas fa-microchip',
    description: 'Actualización del bootloader del locker vía OTA.',
    confirmText: 'Confirmar Bootloader OTA',
    accentColor: '#dc2626',
    bgGradient: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
    warning: 'Acción muy crítica. No interrumpir el proceso bajo ningún concepto.',
  },
}

const OPERATIVE_ACTIONS = ['SINGLE_DOOR_OPEN', 'SINGLE_UNRENT', 'SINGLE_BLOCKLATER', 'SINGLE_RESET_PIN']
const CRITICAL_ACTIONS = ['SINGLE_NIGHTSCAN', 'SINGLE_KERONG_PROGRAM_OTA', 'SINGLE_KERONG_BOOTLOADER_OTA']

class RemoteActionSingleAddForm extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      actionCompleted: false,
      errorMessage: '',
      pendingAction: null,
      cancelled: false,
      showAdvanced: false,
    }

    this.submit = this.submit.bind(this)
    this.selectAction = this.selectAction.bind(this)
    this.cancelAction = this.cancelAction.bind(this)
  }

  async submit(props) {
    props.machine = this.props.machine._id
    props.executor = this.props.fullname

    this.setState({ loading: true, actionCompleted: false, errorMessage: '', cancelled: false })

    try {
      const response = await this.props.addRemoteAction(props)

      if (response.error) {
        throw new SubmissionError({
          _error: response.error.message,
          ...response.error.fields,
        })
      }

      await this.waitForRemoteActionCompletion(response.response._id)
      this.setState({ loading: false, actionCompleted: true })
      window.location.reload()
      return response
    } catch (error) {
      this.setState({ loading: false, errorMessage: error.message || 'Error procesando acción' })
      throw error
    }
  }

  waitForRemoteActionCompletion(actionId) {
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        if (this.state.cancelled) {
          reject(new Error('Acción cancelada por el usuario'))
          return
        }
        try {
          const response = await this.props.fetchRemoteActions(this.props.machine.Serial)
          const action = response.response.find(a => a._id === actionId)
          if (action && (action.status === 'COMPLETE' || action.status === 'ERROR')) {
            resolve(action)
          } else {
            setTimeout(checkStatus, 1000)
          }
        } catch (error) {
          reject(error)
        }
      }
      checkStatus()
    })
  }

  selectAction(code) {
    this.props.dispatch(change('RemoteActionSingleAddForm', 'code', code))
    this.props.dispatch(change('RemoteActionSingleAddForm', 'arguments', `${this.props.lockerMatricula}`))
    this.setState({ pendingAction: code })
  }

  cancelAction() {
    this.props.dispatch(change('RemoteActionSingleAddForm', 'code', ''))
    this.props.dispatch(change('RemoteActionSingleAddForm', 'arguments', ''))
    this.setState({ pendingAction: null })
  }

  render() {
    const { error, pristine, submitting, handleSubmit, machine } = this.props
    const { loading, actionCompleted, pendingAction, errorMessage, showAdvanced } = this.state
    const { lockerMatricula } = this.props

    if (loading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px', gap: 12 }}>
          <span style={{ color: '#64748b', fontWeight: 600, fontSize: 14 }}>Ejecutando acción...</span>
          <LoadingProgress logoHeight={36} className="compact" />
        </div>
      )
    }

    if (actionCompleted) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 16 }}>
          <div className="alert alert-success w-100">Proceso completado</div>
          <div className="alert alert-info w-100">Actualizando...</div>
        </div>
      )
    }

    /* Hidden fields always rendered so redux-form tracks values */
    const hiddenFields = (
      <div style={{ display: 'none' }}>
        <Field name="code" component={renderSelectField}>
          <option value="">-</option>
          <option value="SINGLE_DOOR_OPEN">Abrir</option>
          <option value="SINGLE_UNRENT">Desalquilar</option>
          <option value="SINGLE_BLOCKLATER">Reserva QR/PDA</option>
          <option value="SINGLE_RESET_PIN">Cambiar Pin</option>
          <option value="SINGLE_NIGHTSCAN">Vincular Individual</option>
          <option value="SINGLE_KERONG_PROGRAM_OTA">OTA Programa Individual</option>
          <option value="SINGLE_KERONG_BOOTLOADER_OTA">OTA Bootloader Individual</option>
          <option value="SINGLE_FACTORY_RESET">Reset de fábrica Individual</option>
          <option value="SINGLE_MEMORY_DEBUG">Lectura de memória en Debug</option>
        </Field>
        <Field name="arguments" type="text" component={renderInputField} placeholder="Argumentos" />
      </div>
    )

    const meta = pendingAction ? ACTION_META[pendingAction] : null

    if (pendingAction && meta) {
      return (
        <div>
          {hiddenFields}
          <div style={{ borderRadius: 10, overflow: 'hidden', border: `2px solid ${meta.accentColor}`, boxShadow: `0 4px 20px ${meta.accentColor}33` }}>
            {/* Header */}
            <div style={{ background: meta.bgGradient, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <i className={meta.iconClass} style={{ fontSize: 20, color: '#fff' }}></i>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{meta.label}</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
                  Locker <strong style={{ color: '#fff' }}>{machine ? Number(lockerMatricula) + machine.FirstIndex - 1 : lockerMatricula}</strong> &mdash; {machine && machine.Name}
                </div>
              </div>
            </div>
            {/* Body */}
            <div style={{ background: '#fff', padding: '20px' }}>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, margin: '0 0 14px' }}>
                {meta.description}
              </p>
              {meta.warning && (
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <i className="fas fa-exclamation-triangle" style={{ color: '#d97706', marginTop: 2, flexShrink: 0 }}></i>
                  <span style={{ color: '#92400e', fontSize: 13, lineHeight: 1.5 }}>{meta.warning}</span>
                </div>
              )}
              {(error || errorMessage) && <div className="alert alert-danger mb-3">{error || errorMessage}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={handleSubmit(this.submit)}
                  style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: 'none', background: meta.accentColor, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                >
                  <i className={`${meta.iconClass} mr-2`}></i>{meta.confirmText}
                </button>
                <button
                  type="button"
                  onClick={this.cancelAction}
                  style={{ padding: '11px 22px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div>
        {hiddenFields}

        {/* Operative actions */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Operativas</div>
          <div className="action-grid-2">
            {OPERATIVE_ACTIONS.map(code => {
              const m = ACTION_META[code]
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => this.selectAction(code)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '14px 8px', borderRadius: 8, border: 'none', background: m.bgGradient, color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer', letterSpacing: 0.3, textTransform: 'uppercase' }}
                >
                  <i className={m.iconClass} style={{ fontSize: 18 }}></i>
                  {m.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Critical actions */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Críticas</div>
          <div className="action-grid-3">
            {CRITICAL_ACTIONS.map(code => {
              const m = ACTION_META[code]
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => this.selectAction(code)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 6px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff1f2', color: '#dc2626', fontWeight: 700, fontSize: 10, cursor: 'pointer', letterSpacing: 0.3, textTransform: 'uppercase' }}
                >
                  <i className={m.iconClass} style={{ fontSize: 16 }}></i>
                  {m.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Advanced / custom action */}
        <div>
          <button
            type="button"
            onClick={() => this.setState(s => ({ showAdvanced: !s.showAdvanced }))}
            style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <i className={`fas fa-chevron-${showAdvanced ? 'up' : 'down'}`} style={{ fontSize: 9 }}></i>
            Acción personalizada
          </button>
          {showAdvanced && (
            <form onSubmit={handleSubmit(this.submit)} style={{ marginTop: 8, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <Field name="code" component={renderSelectField}>
                <option value="">Selecciona una acción...</option>
                <option value="REBOOT">Reiniciar máquina</option>
                <option value="SINGLE_DOOR_OPEN">Abrir</option>
                <option value="SINGLE_UNRENT">Desalquilar</option>
                <option value="SINGLE_BLOCKLATER">Reserva QR/PDA</option>
                <option value="SINGLE_RESET_PIN">Cambiar Pin</option>
                <option value="SINGLE_NIGHTSCAN">Vincular Individual</option>
                <option value="SINGLE_KERONG_PROGRAM_OTA">OTA Programa Individual</option>
                <option value="SINGLE_KERONG_BOOTLOADER_OTA">OTA Bootloader Individual</option>
                <option value="SINGLE_FACTORY_RESET">Reset de fábrica Individual</option>
                <option value="SINGLE_MEMORY_DEBUG">Lectura de memória en Debug</option>
              </Field>
              <Field name="arguments" type="text" component={renderInputField} placeholder="Argumentos" />
              <button
                className="btn bg-vw-light text-white w-100 mt-2"
                type="submit"
                disabled={pristine || submitting}
              >
                <i className="fas fa-bolt mr-1"></i>Ejecutar
              </button>
              {error && <div className="alert alert-danger mt-2">{error}</div>}
            </form>
          )}
        </div>
      </div>
    )
  }
}

RemoteActionSingleAddForm = reduxForm({
  form: 'RemoteActionSingleAddForm',
})(RemoteActionSingleAddForm)

const mapStateToProps = state => ({
  auth: state.auth,
  role: state.auth.role,
  type: state.auth.type,
  fullname: state.auth.fullname,
})

const mapDispatchToProps = dispatch => ({
  addRemoteAction: bindActionCreators(addRemoteAction, dispatch),
  fetchRemoteActions: bindActionCreators(fetchRemoteActions, dispatch),
  dispatch,
})

export default connect(mapStateToProps, mapDispatchToProps)(RemoteActionSingleAddForm)

